export default () => {
    const compareNumbers = (a: number, b: number, desc: boolean): number => {
        let cmp: number = 0;
        if (a > b) {
            cmp = 1;
        } else if (b > a) {
            cmp = -1;
        }
        return desc ? cmp * -1 : cmp;
    };

    const compareTankNames = (a: string, b: string, reverseSort?: boolean) => {
        const numbersPattern = /[0-9]/g;
        const lettersPattern = /[a-zA-Z]/g;
        const nA = a.match(numbersPattern)?.join('') || '';
        const nB = b.match(numbersPattern)?.join('') || '';
        const lA = a.match(lettersPattern)?.join('') || '';
        const lB = b.match(lettersPattern)?.join('') || '';

        try {
            if (+nA === +nB) {
                return reverseSort ? lA.localeCompare(lB) * -1 : lA.localeCompare(lB);
            } else {
                // TODO: LOGIC-ERROR - Return value is inconsistent. Should return standardized -1/0/1 values, not the raw difference (+nB - +nA). This makes sorting behavior unpredictable for large number differences.
                return reverseSort ? +nA - +nB : +nB - +nA;
            }
        } catch {
            // TODO: ERROR-HANDLING - Generic catch without error handling. Should specify what errors are expected and handle them appropriately.
            return a.localeCompare(b);
        }
    };

    /**
     * Sorts tank names according to ORCA 8 sorting logic for Last Cargoes report:
     * 1. Group by type - tanks with numeric suffix only come first, then by letter type
     * 2. Within numeric-only tanks, sort by number then by letter suffix (A, B, C, D)
     * 3. Then W (Wing) tanks, then D (Deck) tanks
     * 4. For W/D tanks: sort by number, then by side (P → S)
     *
     * Tank name format: Number + Letter(s)
     * Examples:
     * - Numeric tanks: 1A, 2A, 2B, 3A, 3B, 3C, 3D, 4A, 4B, 4C, 5A, 5B, 6A, 6B, 7A, 7B, 8A, 8B
     * - Wing tanks: 1WP, 1WS, 2WP, 2WS, 3WP, 3WS, etc.
     * - Deck tanks: 1DP, 1DS, 2DP, 2DS, 3DP, 3DS
     * - Or with C (center) tanks: 1CP, 1CS, 2C, 3CP, 3CS, etc.
     */
    const compareTankNamesForLastCargoes = (a: string, b: string): number => {
        // Extract tank number and letter(s)
        const parsePattern = /^(\d+)([A-Z]+)$/;
        const matchA = a.match(parsePattern);
        const matchB = b.match(parsePattern);

        // Fallback to basic comparison if pattern doesn't match
        if (!matchA || !matchB) {
            return a.localeCompare(b);
        }

        const [, numberA, lettersA] = matchA;
        const [, numberB, lettersB] = matchB;

        const numA = parseInt(numberA, 10);
        const numB = parseInt(numberB, 10);

        // Determine tank category:
        // - Single letter (A, B, C, D) = numeric tanks (category 1)
        // - W* = wing tanks (category 2)
        // - D* (when followed by P/S) = deck tanks (category 3)
        // - C* (when followed by P/S or standalone) = center tanks (category 1)

        const isNumericTankA = lettersA.length === 1 && /^[ABCD]$/.test(lettersA);
        const isNumericTankB = lettersB.length === 1 && /^[ABCD]$/.test(lettersB);

        const isCenterTankA = lettersA === 'C' || (lettersA.length === 2 && lettersA[0] === 'C' && /^[PS]$/.test(lettersA[1]));
        const isCenterTankB = lettersB === 'C' || (lettersB.length === 2 && lettersB[0] === 'C' && /^[PS]$/.test(lettersB[1]));

        const isWingTankA = lettersA.length === 2 && lettersA[0] === 'W';
        const isWingTankB = lettersB.length === 2 && lettersB[0] === 'W';

        const isDeckTankA = lettersA.length === 2 && lettersA[0] === 'D' && /^[PS]$/.test(lettersA[1]);
        const isDeckTankB = lettersB.length === 2 && lettersB[0] === 'D' && /^[PS]$/.test(lettersB[1]);

        // Assign categories
        let categoryA = 0;
        let categoryB = 0;

        if (isNumericTankA || isCenterTankA) categoryA = 1;
        else if (isWingTankA) categoryA = 2;
        else if (isDeckTankA) categoryA = 3;

        if (isNumericTankB || isCenterTankB) categoryB = 1;
        else if (isWingTankB) categoryB = 2;
        else if (isDeckTankB) categoryB = 3;

        // Compare by category first
        if (categoryA !== categoryB) {
            return categoryA - categoryB;
        }

        // Within same category, compare by number
        if (numA !== numB) {
            return numA - numB;
        }

        // Within same number, sort by letter suffix
        // For numeric tanks (1A, 2B, etc.) or center tanks, sort alphabetically
        // For wing/deck tanks with P/S suffix, P comes before S

        if (isNumericTankA || isNumericTankB) {
            // Simple alphabetic sort for A, B, C, D
            return lettersA.localeCompare(lettersB);
        }

        if (isCenterTankA || isCenterTankB) {
            // For center tanks: single 'C' comes before 'CP' and 'CS'
            // Then CP before CS
            if (lettersA === 'C' && lettersB !== 'C') return -1;
            if (lettersA !== 'C' && lettersB === 'C') return 1;
            return lettersA.localeCompare(lettersB);
        }

        if (isWingTankA || isDeckTankA) {
            // For wing and deck tanks, compare by side: P before S
            const sideA = lettersA[1];
            const sideB = lettersB[1];
            const sideOrder: Record<string, number> = { P: 1, S: 2 };
            return (sideOrder[sideA] || 99) - (sideOrder[sideB] || 99);
        }

        // Fallback
        return lettersA.localeCompare(lettersB);
    };

    return {
        compareNumbers,
        compareTankNames,
        compareTankNamesForLastCargoes,
    };
};
